/* eslint formatjs/no-literal-string-in-jsx: "error" */
import React from "react"
import { useFormContext } from "react-hook-form"
import { useSignUp } from "./context/SignUpContext"
import { AppAccessPageLayout } from "components/access-page/AppAccessPageLayout"
import {
  TextField,
  Button,
  Typography,
  Stack,
  Divider,
  useTheme,
  Box,
  Alert,
  Link
} from "@carrotfertility/carotene-core"
import {
  AppAccessPageCard,
  AppAccessPageCardFooter,
  AppAccessPageCardHeader
} from "components/access-page/AppAccessPageCard"
import { Form } from "components/views/atoms/forms/Form"
import { UnauthPreferredLocale } from "components/views/UnauthPreferredLocale"
import { emailValidation } from "utils/Regexes"
import { useIntlValidator } from "utils/hooks/useIntlValidator"
import { FormattedMessage, useIntl } from "react-intl"
import { getErrorMessageFromCode } from "../../utils/CarrotErrors"

const EmailInput = ({ enabled, email }): JSX.Element => {
  const {
    register,
    formState: { errors }
  } = useFormContext()
  const intlEmailValidation = useIntlValidator(emailValidation)
  const intl = useIntl()

  return (
    <TextField
      InputLabelProps={{ sx: { textAlign: "left" } }}
      disabled={!enabled}
      inputProps={{ readOnly: !enabled, disabled: false }}
      {...register("email", {
        required: intl.formatMessage({ defaultMessage: "Email is required" }),
        validate: intlEmailValidation
      })}
      helperText={errors.email?.message}
      error={Boolean(errors.email)}
      defaultValue={email}
      id="email"
      label={intl.formatMessage({ defaultMessage: "Email" })}
      type="email"
    />
  )
}

export const SignUpFooter = (): JSX.Element => {
  const intl = useIntl()
  return (
    <AppAccessPageCardFooter
      links={[
        {
          id: "access-page-sign-in-link",
          to: "/",
          linkText: intl.formatMessage({ defaultMessage: "Already have an account? Sign in" })
        },
        {
          id: "access-page-offer-carrot-learn-more-link",
          to:
            "https://www.get-carrot.com/landing-pages/for-employees-request-fertility-benefits-at-your-company?utm_medium=app&utm_source=cf&utm_campaign=dg",
          linkText: intl.formatMessage({ defaultMessage: "Request Carrot at your company" }),
          external: true
        }
      ]}
    />
  )
}

const SignUpPage = (): JSX.Element => {
  const { shake, inputsEnabled, showSubmittingLabel, username, onSubmit, errorMessage } = useSignUp()
  const theme = useTheme()
  const intl = useIntl()

  const getTranslatedErrorMessage = (errorCode) => {
    if (errorCode === "MEMBER_INACTIVE") {
      return intl.formatMessage(getErrorMessageFromCode(errorCode), {
        linkContent: "support@get-carrot.com",
        link: (string) => (
          <Link color="inherit" href="mailto:support@get-carrot.com">
            {string}
          </Link>
        )
      })
    }
    return intl.formatMessage(getErrorMessageFromCode(errorCode))
  }

  return (
    <AppAccessPageLayout title={intl.formatMessage({ defaultMessage: "Sign up" })}>
      <AppAccessPageCard shake={shake}>
        <AppAccessPageCardHeader>
          {errorMessage ? (
            <Alert severity="error">{getTranslatedErrorMessage(errorMessage)}</Alert>
          ) : (
            <Box textAlign="center">
              <Typography variant="h1">
                <FormattedMessage defaultMessage="Sign up for Carrot" />
              </Typography>
              <Typography>
                <FormattedMessage defaultMessage="Enter your company email address or the address your company uses for you." />
              </Typography>
            </Box>
          )}
        </AppAccessPageCardHeader>
        <Form noValidate onSubmit={onSubmit}>
          <Stack divider={<Divider />} spacing={theme.spacing(theme.tokens.spacing.lg)}>
            <Stack
              spacing={theme.spacing(theme.tokens.spacing.lg)}
              display="block"
              textAlign="center"
              marginBottom={theme.spacing(theme.tokens.spacing.xl)}
            >
              <EmailInput enabled={inputsEnabled} email={username} />
              <Button onClick={onSubmit} disabled={showSubmittingLabel}>
                <FormattedMessage defaultMessage="Continue" />
              </Button>
              <Stack spacing={theme.spacing(theme.tokens.spacing.lg)} display="block">
                <UnauthPreferredLocale />
              </Stack>
            </Stack>
            <SignUpFooter />
          </Stack>
        </Form>
      </AppAccessPageCard>
    </AppAccessPageLayout>
  )
}

export default SignUpPage
